import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  StockMovement,
  StockMovementCreate,
  StockMovementUpdate,
  StockMovementTransferRequest,
  StockMovementApprovalRequest,
  StockMovementRejectionRequest,
  TransferValidationRequest,
  StockMovementStats,
  StockMovementByPeriod
} from '../models/StockMovement';
import { ErrorService } from '../shared/services/error.service';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';

export type StockMovementPagedResult = PagedResult<StockMovement>;

export interface ServiceOrderStockRequestItemDetail {
  serviceOrderStockRequestItemId: number;
  requestedStockId: number;
  requestedStockName?: string;
  requestedQuantity: number;
  status: string;
  sourceWarehouseId?: number;
  sourceWarehouseName?: string;
  reservedQuantity?: number;
  fulfilledQuantity?: number;
  notes?: string;
}

export interface ServiceOrderStockRequestHistoryDetail {
  serviceOrderStockRequestHistoryId: number;
  serviceOrderStockRequestItemId?: number;
  performedByStaffId?: number;
  eventDate: string;
  eventType: string;
  toStatus?: string;
  quantity?: number;
  description?: string;
}

export interface ServiceOrderStockRequestDetail {
  serviceOrderStockRequestId: number;
  requestNumber: string;
  serviceOrderId: number;
  serviceOrderNumber?: string;
  requestedByStaffId?: number;
  requestedAt: string;
  status: string;
  priority: string;
  notes?: string;
  items: ServiceOrderStockRequestItemDetail[];
  history: ServiceOrderStockRequestHistoryDetail[];
}

export interface FulfillServiceOrderStockRequestItemPayload {
  serviceOrderStockRequestItemId: number;
  itemId: number;
  quantity: number;
  originWarehouseId: number;
  reason: string;
  notes?: string;
}

export interface FulfillServiceOrderStockRequestBatchPayload {
  serviceOrderStockRequestId: number;
  reason: string;
  notes?: string;
  items: FulfillServiceOrderStockRequestItemPayload[];
}

export interface UpdateServiceOrderStockRequestStatusPayload {
  status: string;
  description?: string;
}

export interface CreateServiceOrderStockRequestItemPayload {
  requestedStockId: number;
  requestedQuantity: number;
  sourceWarehouseId?: number;
  notes?: string;
}

export interface CreateServiceOrderStockRequestPayload {
  serviceOrderId: number;
  priority: string;
  notes?: string;
  items: CreateServiceOrderStockRequestItemPayload[];
}

export interface ManualConsumptionExitBatchPayload {
  itemId: number;
  quantity: number;
  originWarehouseId: number;
  reason: string;
  notes?: string;
  staffId?: number;
  requiresApproval: boolean;
  attachments: string[];
}

export interface ManualStockMovementBatchLine {
  itemId: number;
  warehouseId: number;
  quantity: number;
  note?: string;
}

export interface ManualStockMovementBatchPayload {
  direction: 'Entry' | 'Exit';
  requiresApproval: boolean;
  notes?: string;
  lines: ManualStockMovementBatchLine[];
  receipt?: {
    documentNumber?: string;
    documentSeries?: string;
    invoiceAccessKey?: string;
    fiscalDocumentUrl?: string | null;
    note?: string;
  };
}

/**
 * Linha do payload de entrada aguardando conferência
 * (POST `/stock-movements/entry-awaiting-receipt`).
 *
 * O `expectedItem` descreve o que se espera receber — o estoque cria a
 * movimentação no status «aguardando recebimento» e só consolida quando o
 * almoxarife confirmar a conferência (fluxo `confirm-entry-receipt`).
 */
export interface EntryAwaitingReceiptExpectedItem {
  name: string;
  /**
   * Código interno do item. Opcional — o operador pode não conhecer o
   * código, ou o item pode ainda não estar cadastrado em Itens. Quando
   * ausente, o backend trata como entrada genérica para conferência manual.
   */
  code?: string;
  supplierCode?: string;
  unitOfMeasure: string;
  estimatedUnitCost: number;
  category?: string;
}

export interface EntryAwaitingReceiptLine {
  expectedItem: EntryAwaitingReceiptExpectedItem;
  quantity: number;
  warehouseId: number;
  note?: string;
}

export interface EntryAwaitingReceiptPayload {
  notes?: string;
  lines: EntryAwaitingReceiptLine[];
}

/** PUT .../pending-item-requests/{pendingItemEntryId}/link-item — body `{ "itemId": number }` */
export interface LinkPendingItemRequestPayload {
  itemId: number;
}

/** POST .../stock-movements/{id}/confirm-entry-receipt */
export interface ConfirmEntryReceiptPayload {
  /** Item de estoque que está entrando (quando a API exige no corpo). */
  itemId?: number | null;
  receivedQuantity: number;
  conferenceNote?: string | null;
  purchaseOrderId?: number | null;
  purchaseOrderItemId?: number | null;
  receiptStatus?: string | null;
  receiptRowNotes?: string | null;
  receivedAt?: string | null;
  receivedByStaffId?: number | null;
  invoiceAccessKey?: string | null;
  receipt: {
    documentNumber: string;
    documentSeries?: string | null;
    invoiceAccessKey?: string | null;
    fiscalDocumentUrl?: string | null;
    note?: string | null;
  };
}

@Injectable({
  providedIn: 'root'
})
export class StockMovementsService {
  /** Base única das rotas de estoque, respeitando o environment ativo (dev/prod/local). */
  private get movementRoot(): string {
    return (environment.microservices?.stock || '').replace(/\/$/, '');
  }

  private get baseUrl() {
    return this.movementRoot;
  }

  private get movementRegistrationUrl() {
    return `${this.movementRoot}/service-order/stock/movements`;
  }

  private get serviceOrderRequestsUrl() {
    return `${this.movementRoot}/service-order-stock-requests`;
  }

  private get listUrl() {
    return `${this.movementRoot}/stock-movements/entry-receipt`;
  }

  /** Listagem geral: GET {{stockBase}}/stock-movements?page=&pageSize= */
  private get pagedAllMovementsUrl(): string {
    return `${this.movementRoot}/stock-movements`;
  }

  /** Saída manual de consumo (novo fluxo) */
  private get manualBatchUrl(): string {
    return `${this.movementRoot}/stock-movements/manual-batch`;
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private authService: AuthService
  ) {}

  /**
   * Indica se uma entrada ainda aceita registro de recebimento (conferência / NF).
   * Inclui «em trânsito» (status ou transferStatus), quando a mercadoria já pode ser conferida,
   * e estados «aguardando recebimento». A API pode usar rótulos abreviados ou pt-BR.
   */
  isOpenForEntryReceipt(movement: {
    status?: string;
    movementType?: string;
    transferStatus?: string | null;
  }): boolean {
    if (!this.isEntryMovementType(movement.movementType)) {
      return false;
    }
    if (this.isEntryInTransitPhase(movement)) {
      return true;
    }
    const tsNorm = this.normalizeKey(movement.transferStatus);
    if (
      tsNorm === 'awaitingreceipt' ||
      (tsNorm.startsWith('awaiting') && tsNorm.includes('receipt'))
    ) {
      return true;
    }
    const key = this.normalizeKey(movement.status);
    if (!key) {
      return false;
    }
    if (key === 'awaitingreceipt' || key === 'recparcialpend') {
      return true;
    }
    if (key === 'aguardandorec' || key === 'aguardandorecebimento' || key === 'awaitingrec') {
      return true;
    }
    if (key.startsWith('aguardando') && (key.includes('receb') || key.includes('recep') || /rec$/.test(key))) {
      return true;
    }
    if (key.startsWith('awaiting') && (key.includes('receipt') || /rec$/.test(key))) {
      return true;
    }
    return false;
  }

  /**
   * Entrada na fase logística «em trânsito» (ou equivalente), antes do recebimento.
   * A API pode enviar o estado só em `status` (pt-BR / enums) sem preencher `transferStatus`.
   */
  isEntryInTransitPhase(movement: {
    status?: string;
    movementType?: string;
    transferStatus?: string | null;
  }): boolean {
    if (!this.isEntryMovementType(movement.movementType)) {
      return false;
    }
    const ts = this.normalizeKey(movement.transferStatus);
    if (ts) {
      if (ts === 'intransit' || ts.includes('intransit') || ts.includes('transito')) {
        return true;
      }
    }
    const key = this.normalizeKey(movement.status);
    if (!key) {
      return false;
    }
    if (key.includes('intransit')) {
      return true;
    }
    if (key.includes('transito')) {
      return true;
    }
    if (key === 'emtransito') {
      return true;
    }
    return false;
  }

  private isEntryMovementType(raw?: string): boolean {
    const t = this.normalizeKey(raw);
    if (!t) {
      return true;
    }
    if (t === 'entry' || t === 'entrada') {
      return true;
    }
    if (['exit', 'saida', 'transfer', 'transferencia', 'adjustment', 'ajuste'].includes(t)) {
      return false;
    }
    return false;
  }

  private normalizeKey(value: string | undefined | null): string {
    if (value == null) {
      return '';
    }
    return value
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  // ===== CRUD OPERATIONS =====

  getAll(options?: PaginationOptions): Observable<StockMovementPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.listUrl, {
      params,
      headers: this.buildJwtHeaders()
    }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar movimentações de estoque');
        return throwError(() => err);
      })
    );
  }

  /**
   * Lista paginada de movimentações (tipos variados) — GET /stock-movements
   * Usado no ecrã "Todas as movimentações" (inclui saídas, entradas, etc.).
   */
  getAllStockMovementsPage(options?: PaginationOptions): Observable<StockMovementPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', String(options.page));
    if (options?.pageSize) params = params.set('pageSize', String(options.pageSize));

    return this.http.get<any>(this.pagedAllMovementsUrl, {
      params,
      headers: this.buildJwtHeaders()
    }).pipe(
      map((resp) => this.mapPagedResponse(resp)),
      catchError((err) => {
        this.errorService.showErrorMessage('Erro ao buscar movimentações de estoque');
        return throwError(() => err);
      })
    );
  }

  getServiceOrderStockRequests(options?: PaginationOptions): Observable<StockMovementPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(this.serviceOrderRequestsUrl, { params, headers: this.buildJwtHeaders() }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar solicitações de estoque da OS');
        return throwError(() => err);
      })
    );
  }

  getServiceOrderStockRequestById(requestId: number): Observable<ServiceOrderStockRequestDetail> {
    return this.http.get<ServiceOrderStockRequestDetail>(`${this.serviceOrderRequestsUrl}/${requestId}`, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao carregar detalhes da solicitação de estoque');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria uma solicitação de estoque para OS.
   * POST {{stockBase}}/service-order-stock-requests
   */
  createServiceOrderStockRequest(payload: CreateServiceOrderStockRequestPayload): Observable<ServiceOrderStockRequestDetail> {
    return this.http.post<ServiceOrderStockRequestDetail>(this.serviceOrderRequestsUrl, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar solicitação de estoque da OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST {{apiBase}}/service-order-stock-requests/{serviceOrderStockRequestId}/fulfill-item
   * Ex.: /api/service-order-stock-requests/3/fulfill-item
   */
  fulfillServiceOrderStockRequestItem(
    serviceOrderStockRequestId: number,
    payload: FulfillServiceOrderStockRequestItemPayload
  ): Observable<any> {
    return this.http.post<any>(
      `${this.serviceOrderRequestsUrl}/${serviceOrderStockRequestId}/fulfill-item`,
      payload,
      { headers: this.buildJwtHeaders() }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atender item da solicitação de estoque');
        return throwError(() => err);
      })
    );
  }

  fulfillServiceOrderStockRequestBatch(payload: FulfillServiceOrderStockRequestBatchPayload): Observable<any> {
    return this.http.post<any>(`${this.serviceOrderRequestsUrl}/fulfill`, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atender itens da solicitação de estoque');
        return throwError(() => err);
      })
    );
  }

  updateServiceOrderStockRequestStatus(
    requestId: number,
    payload: UpdateServiceOrderStockRequestStatusPayload
  ): Observable<any> {
    return this.http.put<any>(`${this.serviceOrderRequestsUrl}/${requestId}/status`, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar status da solicitação de estoque');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<StockMovement> {
    return this.http.get<any>(`${this.baseUrl}/stock-movements/${id}`, {
      headers: this.buildJwtHeaders()
    }).pipe(
      map((raw) => {
        const dto = this.unwrapStockMovementEnvelope(raw);
        return this.enrichMovementFromDtoRoot(dto, this.mapFromDto(dto));
      }),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentação de estoque'); return throwError(() => err); })
    );
  }

  create(payload: StockMovementCreate): Observable<StockMovement> {
    const dto = this.mapToDto(payload);
    return this.http.post<any>(this.movementRegistrationUrl, dto, { params: this.buildCreatedByParams() }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao criar movimentação de estoque'); return throwError(() => err); })
    );
  }

  createManualHybrid(
    payload: {
      movementType: 'Entrada' | 'Saida';
      itemId: number;
      quantity: number;
      originWarehouseId?: number | null;
      destinationWarehouseId?: number | null;
      reason: string;
      notes?: string | null;
      staffId?: number;
      requiresApproval?: boolean;
      serviceOrderId?: number;
    },
    attachments: string[] = []
  ): Observable<StockMovement> {
    const params = this.buildCreatedByParams();
    const sanitizedAttachments = attachments.filter(url => !!url && url.trim().length > 0);

    const isEntry = payload.movementType === 'Entrada';
    const reasonExit = (payload.reason || '').trim() || 'Consumo OS';
    const notesExit = (payload.notes != null && String(payload.notes).trim() !== '')
      ? String(payload.notes).trim()
      : 'Saida manual';

    /**
     * Saída manual: corpo “flat” (não é transferência para outro almoxarifado — só saida a partir do origem).
     */
    const requestBody = isEntry
      ? {
          movement: {
            itemId: payload.itemId,
            movementType: 'Entry',
            quantity: payload.quantity,
            destinationWarehouseId: payload.destinationWarehouseId,
            reason: payload.reason,
            notes: payload.notes || undefined,
            serviceOrderId: payload.serviceOrderId || undefined,
            ...(payload.staffId ? { staffId: payload.staffId } : {}),
            requiresApproval: false
          },
          attachments: sanitizedAttachments
        }
      : {
          itemId: Number(payload.itemId),
          movementType: 'Exit' as const,
          quantity: Number(payload.quantity),
          originWarehouseId: Number(payload.originWarehouseId),
          reason: reasonExit,
          notes: notesExit,
          serviceOrderId: payload.serviceOrderId || undefined,
          ...(payload.staffId ? { staffId: Number(payload.staffId) } : {}),
          requiresApproval: payload.requiresApproval ?? true,
          attachments: sanitizedAttachments
        };

    return this.http.post<any>(this.movementRegistrationUrl, requestBody, { params }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar movimentação manual de estoque');
        return throwError(() => err);
      })
    );
  }

  createManualConsumptionExitBatch(payload: ManualConsumptionExitBatchPayload): Observable<StockMovement> {
    const lineNote = (payload.reason || '').trim() || (payload.notes || '').trim() || 'Saida manual';
    const batchNotes = (payload.notes || '').trim() || (payload.reason || '').trim() || 'Saida manual';
    const body = {
      direction: 'Exit',
      requiresApproval: payload.requiresApproval ?? true,
      notes: batchNotes,
      lines: [
        {
          itemId: Number(payload.itemId),
          quantity: Number(payload.quantity),
          warehouseId: Number(payload.originWarehouseId),
          note: lineNote
        }
      ]
    };

    return this.http.post<any>(this.manualBatchUrl, body, {
      headers: this.buildJwtHeaders(),
      params: this.buildCreatedByParams()
    }).pipe(
      map((resp) => this.mapFromDto(resp)),
      catchError((err) => {
        this.errorService.showErrorMessage('Erro ao criar saída manual de consumo');
        return throwError(() => err);
      })
    );
  }

  createManualBatch(payload: ManualStockMovementBatchPayload): Observable<StockMovement> {
    return this.http.post<any>(this.manualBatchUrl, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      map((resp) => this.mapFromDto(resp)),
      catchError((err) => {
        this.errorService.showErrorMessage('Erro ao criar movimentação manual em lote');
        return throwError(() => err);
      })
    );
  }

  private buildCreatedByParams(): HttpParams {
    const uniqueName = this.authService.getUniqueNameFromToken().trim();
    return uniqueName ? new HttpParams().set('createdBy', uniqueName) : new HttpParams();
  }

  private buildJwtHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();
  }

  update(id: number, payload: StockMovementUpdate): Observable<StockMovement> {
    const dto = this.mapToDto(payload);
    return this.http.put<any>(`${this.baseUrl}/stock-movements/${id}`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao atualizar movimentação de estoque'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/stock-movements/${id}`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao excluir movimentação de estoque'); return throwError(() => err); })
    );
  }

  // ===== CALCULATIONS =====

  getCurrentStock(itemId: number): Observable<{ itemId: number; currentStock: number }> {
    return this.http.get<{ itemId: number; currentStock: number }>(`${this.baseUrl}/stock-movements/current-stock/${itemId}`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoque atual'); return throwError(() => err); })
    );
  }

  // ===== SPECIFIC QUERIES =====

  getByItem(itemId: number): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/item/${itemId}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por item'); return throwError(() => err); })
    );
  }

  getByType(type: string): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/type/${encodeURIComponent(type)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por tipo'); return throwError(() => err); })
    );
  }

  getByPeriod(startDate: string, endDate: string): Observable<StockMovement[]> {
    const params = new HttpParams()
      .set('start', startDate)
      .set('end', endDate);

    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/period`, { params }).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por período'); return throwError(() => err); })
    );
  }

  getByStatus(status: string): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/status/${encodeURIComponent(status)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por status'); return throwError(() => err); })
    );
  }

  getByTransferStatus(status: string): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/transfer-status/${encodeURIComponent(status)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por status de transferência'); return throwError(() => err); })
    );
  }

  getByServiceOrder(serviceOrderId: number): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/service-order/${serviceOrderId}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por ordem de serviço'); return throwError(() => err); })
    );
  }

  getByWarehouse(warehouseId: number): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/warehouse/${warehouseId}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por almoxarifado'); return throwError(() => err); })
    );
  }

  getByStaff(staffId: number): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/staff/${staffId}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações por funcionário'); return throwError(() => err); })
    );
  }

  getPendingApproval(): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/pending-approval`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar movimentações pendentes de aprovação'); return throwError(() => err); })
    );
  }

  // ===== OPERATIONS =====

  executeMovement(id: number): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.baseUrl}/stock-movements/${id}/execute`, {}, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao executar movimentação de estoque'); return throwError(() => err); })
    );
  }

  /**
   * Confirma recebimento de entrada (NF / conferência).
   * POST {{baseUrl}}/stock-movements/{id}/confirm-entry-receipt
   */
  confirmEntryReceipt(id: number, payload: ConfirmEntryReceiptPayload): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/stock-movements/${id}/confirm-entry-receipt`, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao confirmar recebimento de entrada');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria entradas de estoque «aguardando conferência» a partir de um pedido
   * de compra indicado. Cada linha descreve o item esperado (nome, código,
   * unidade, custo estimado) e o almoxarifado destino. O almoxarife depois
   * confirma o recebimento via `confirmEntryReceipt`.
   *
   * POST {{stockBase}}/stock-movements/entry-awaiting-receipt
   */
  entryAwaitingReceipt(payload: EntryAwaitingReceiptPayload): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/stock-movements/entry-awaiting-receipt`, payload, {
        headers: this.buildJwtHeaders()
      })
      .pipe(
        catchError((err) => {
          this.errorService.showErrorMessage(
            'Erro ao registrar entrada aguardando conferência no estoque'
          );
          return throwError(() => err);
        })
      );
  }

  /**
   * Vincula um item cadastrado no estoque a uma linha de entrada pendente
   * (pedido de compra / entrada aguardando vínculo). Após sucesso, o fluxo
   * avança (ex.: de «solicitado» para «em trânsito»), conforme regra do backend.
   *
   * PUT {{stockBase}}/stock-movements/pending-item-requests/{pendingItemEntryId}/link-item
   * Body: `{ "itemId": number }`
   *
   * Quando a API devolve o recurso atualizado no corpo, mapeamos para `StockMovement`;
   * caso contrário o cliente deve recarregar com `getById`.
   */
  linkPendingItemRequest(
    pendingItemEntryId: number,
    payload: LinkPendingItemRequestPayload
  ): Observable<StockMovement | null> {
    const id = Number(pendingItemEntryId);
    if (!Number.isFinite(id) || id <= 0) {
      return throwError(() => new Error('Identificador da entrada pendente inválido.'));
    }
    const itemId = Number(payload?.itemId);
    if (!Number.isFinite(itemId) || itemId <= 0) {
      return throwError(() => new Error('Selecione um item de estoque válido.'));
    }
    const url = `${this.baseUrl}/stock-movements/pending-item-requests/${id}/link-item`;
    return this.http.put<any>(url, { itemId }, { headers: this.buildJwtHeaders() }).pipe(
      map((body: any) => {
        if (body == null || typeof body !== 'object') {
          return null;
        }
        const raw = body?.movement ?? body?.Movement ?? body;
        const rid = raw?.Id ?? raw?.id;
        if (rid == null || Number(rid) <= 0) {
          return null;
        }
        return this.mapFromDto(body);
      }),
      catchError((err) => {
        this.errorService.showErrorMessage('Erro ao vincular item à entrada pendente');
        return throwError(() => err);
      })
    );
  }

  approveMovement(id: number, request: StockMovementApprovalRequest): Observable<{ message: string }> {
    const payload = { justification: (request?.justification || '').trim() };
    return this.http.post<{ message: string }>(`${this.baseUrl}/stock-movements/${id}/approve`, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao aprovar movimentação de estoque'); return throwError(() => err); })
    );
  }

  rejectMovement(id: number, request: StockMovementRejectionRequest): Observable<{ message: string }> {
    const payload = { justification: (request?.justification || '').trim() };
    return this.http.post<{ message: string }>(`${this.baseUrl}/stock-movements/${id}/reject`, payload, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao rejeitar movimentação de estoque'); return throwError(() => err); })
    );
  }

  transferMovement(request: StockMovementTransferRequest): Observable<StockMovement> {
    return this.http.post<any>(`${this.baseUrl}/stock-movements/transfer`, request).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao transferir estoque'); return throwError(() => err); })
    );
  }

  getTransferHistory(itemId: number): Observable<StockMovement[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-movements/transfer-history/${itemId}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar histórico de transferências'); return throwError(() => err); })
    );
  }

  validateTransfer(request: TransferValidationRequest): Observable<{ isValid: boolean; message: string }> {
    return this.http.get<{ isValid: boolean; message: string }>(
      `${this.baseUrl}/stock-movements/validate-transfer/${request.itemId}/${request.quantity}/${request.originWarehouseId}/${request.destinationWarehouseId}`
    ).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao validar transferência'); return throwError(() => err); })
    );
  }

  // ===== STATISTICS =====

  getStats(): Observable<StockMovementStats> {
    return this.http.get<StockMovementStats>(`${this.baseUrl}/stock-movements/stats`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estatísticas de movimentações'); return throwError(() => err); })
    );
  }

  getStatsByPeriod(startDate: string, endDate: string): Observable<StockMovementByPeriod[]> {
    const params = new HttpParams()
      .set('start', startDate)
      .set('end', endDate);

    return this.http.get<StockMovementByPeriod[]>(`${this.baseUrl}/stock-movements/stats/period`, { params }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estatísticas por período'); return throwError(() => err); })
    );
  }

  // ===== MAPPING METHODS =====

  /** Normaliza envelopes comuns (`data`, `result`) para um objeto plano com `movement` + campos na raiz. */
  private unwrapStockMovementEnvelope(raw: any): any {
    if (!raw || typeof raw !== 'object') {
      return raw;
    }
    const bag = raw.data ?? raw.Data ?? raw.result ?? raw.Result ?? raw.payload ?? raw.Payload;
    if (bag && typeof bag === 'object') {
      return { ...raw, ...bag };
    }
    return raw;
  }

  /** Vários nós onde a API pode colocar os mesmos campos (raiz, `data`, objeto `movement`). */
  private collectStockMovementDtoRoots(dto: any): any[] {
    const roots: any[] = [];
    const push = (o: any) => {
      if (o && typeof o === 'object' && !roots.includes(o)) {
        roots.push(o);
      }
    };
    push(dto);
    push(dto?.data);
    push(dto?.Data);
    push(dto?.movement);
    push(dto?.Movement);
    push(dto?.data?.movement);
    push(dto?.Data?.Movement);
    return roots;
  }

  private pickFirstLineFromRoots(roots: any[]): any {
    for (const r of roots) {
      const lines = r?.lines ?? r?.Lines ?? r?.entryLines ?? r?.EntryLines;
      if (Array.isArray(lines) && lines.length > 0 && lines[0]) {
        return lines[0];
      }
    }
    return null;
  }

  private pickFromRoots(roots: any[], keys: string[]): any {
    for (const r of roots) {
      const v = keys.map((k) => r?.[k]).find((x) => x !== undefined && x !== null && x !== '');
      if (v !== undefined) {
        return v;
      }
    }
    return undefined;
  }

  private pickNonEmptyStringFromRoots(roots: any[], keys: string[]): string | undefined {
    const v = this.pickFromRoots(roots, keys);
    if (v === undefined || v === null) {
      return undefined;
    }
    const s = String(v).trim();
    return s || undefined;
  }

  /**
   * Mescla campos do envelope JSON (raiz do DTO) como na listagem paginada,
   * para que GET por id traga nome/código do item esperado da mesma forma.
   */
  private enrichMovementFromDtoRoot(dto: any, m: StockMovement): StockMovement {
    const roots = this.collectStockMovementDtoRoots(dto);
    const num = (v: any): number | undefined => {
      if (v === undefined || v === null || v === '') {
        return undefined;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const pickStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.trim() ? v.trim() : undefined;

    const itemNameKeys = [
      'ItemName',
      'itemName',
      'ProductName',
      'productName',
      'ItemDescription',
      'itemDescription',
      'Description',
      'description',
      'StockItemName',
      'stockItemName',
      'expectedItemName',
      'ExpectedItemName'
    ];
    const itemCodeKeys = [
      'ItemCode',
      'itemCode',
      'ProductCode',
      'productCode',
      'InternalCode',
      'internalCode',
      'StockCode',
      'stockCode',
      'expectedItemCode',
      'ExpectedItemCode'
    ];
    const stockNameKeys = ['stockName', 'StockName', 'StockItemName', 'stockItemName'];
    const stockCodeKeys = ['stockCode', 'StockCode'];
    const topItemWarehouseName = this.pickFromRoots(roots, ['itemWarehouseName', 'ItemWarehouseName']);
    const init = num(this.pickFromRoots(roots, ['initialExpectedQuantity', 'InitialExpectedQuantity']));
    const pend = num(this.pickFromRoots(roots, ['pendingQuantity', 'PendingQuantity']));
    const recv = num(this.pickFromRoots(roots, ['receivedQuantity', 'ReceivedQuantity']));
    const topPending = num(
      this.pickFromRoots(roots, [
        'pendingItemEntryId',
        'PendingItemEntryId',
        'pendingItemRequestEntryId',
        'PendingItemRequestEntryId',
        'pendingItemRequestId',
        'PendingItemRequestId'
      ])
    );
    const topItemId = num(this.pickFromRoots(roots, ['ItemId', 'itemId', 'StockItemId', 'stockItemId']));
    const linkedItemIdFromDto = num(this.pickFromRoots(roots, ['LinkedItemId', 'linkedItemId']));
    const linkedItemNameFromDto = this.pickNonEmptyStringFromRoots(roots, ['LinkedItemName', 'linkedItemName']);
    const linkedItemCodeFromDto = this.pickNonEmptyStringFromRoots(roots, ['LinkedItemCode', 'linkedItemCode']);

    const line0 = this.pickFirstLineFromRoots(roots);
    const exp =
      dto?.expectedItem ??
      dto?.ExpectedItem ??
      line0?.expectedItem ??
      line0?.ExpectedItem ??
      roots.map((r) => r?.expectedItem ?? r?.ExpectedItem).find((x) => x && typeof x === 'object');
    let expectedItemName: string | undefined;
    let expectedItemCode: string | undefined;
    if (exp && typeof exp === 'object') {
      const pick = (o: any, keys: string[]) => keys.map((k) => o?.[k]).find((v) => v !== undefined && v !== null);
      const n = pick(exp, ['name', 'Name', 'description', 'Description', 'productName', 'ProductName']);
      const c = pick(exp, ['code', 'Code', 'supplierCode', 'SupplierCode', 'internalCode', 'InternalCode']);
      expectedItemName = pickStr(n);
      expectedItemCode = pickStr(c);
    }

    const topItemName = this.pickNonEmptyStringFromRoots(roots, itemNameKeys);
    const topItemCode = this.pickNonEmptyStringFromRoots(roots, itemCodeKeys);
    const topStockName = this.pickNonEmptyStringFromRoots(roots, stockNameKeys);
    const topStockCode = this.pickNonEmptyStringFromRoots(roots, stockCodeKeys);

    const supplierOrVendor = this.pickNonEmptyStringFromRoots(roots, [
      'SupplierName',
      'supplierName',
      'VendorName',
      'vendorName',
      'SupplierCompanyName',
      'supplierCompanyName',
      'CompanyName',
      'companyName',
      'OriginSupplierName',
      'originSupplierName'
    ]);
    const originWhName = this.pickNonEmptyStringFromRoots(roots, [
      'OriginWarehouseName',
      'originWarehouseName',
      'SourceWarehouseName',
      'sourceWarehouseName',
      'originName',
      'OriginName'
    ]);
    const destWhName = this.pickNonEmptyStringFromRoots(roots, [
      'DestinationWarehouseName',
      'destinationWarehouseName',
      'TargetWarehouseName',
      'targetWarehouseName'
    ]);

    const resolvedName =
      linkedItemNameFromDto ||
      topItemName ||
      expectedItemName ||
      topStockName ||
      m.itemName;
    const resolvedCode =
      linkedItemCodeFromDto || topItemCode || expectedItemCode || topStockCode || m.itemCode;

    const effectiveItemId =
      m.itemId != null && m.itemId > 0
        ? m.itemId
        : topItemId !== undefined && topItemId > 0
          ? topItemId
          : linkedItemIdFromDto !== undefined && linkedItemIdFromDto > 0
            ? linkedItemIdFromDto
            : m.itemId;

    const resolvedOriginName =
      originWhName ||
      supplierOrVendor ||
      (m.originWarehouseName && String(m.originWarehouseName).trim()) ||
      undefined;
    const resolvedDestName =
      destWhName || (m.destinationWarehouseName && String(m.destinationWarehouseName).trim()) || undefined;

    const topPoNum = this.pickNonEmptyStringFromRoots(roots, [
      'PurchaseOrderNumber',
      'purchaseOrderNumber',
      'OrderNumber',
      'orderNumber',
      'PoNumber',
      'poNumber'
    ]);
    const topPoId = num(this.pickFromRoots(roots, ['PurchaseOrderId', 'purchaseOrderId', 'PoId', 'poId']));
    const topPoLineId = num(
      this.pickFromRoots(roots, ['PurchaseOrderItemId', 'purchaseOrderItemId', 'PurchaseRequestItemId', 'purchaseRequestItemId'])
    );

    return {
      ...m,
      itemId: effectiveItemId,
      itemName: resolvedName,
      itemCode: resolvedCode,
      stockName: topStockName != null ? String(topStockName) : undefined,
      stockCode: topStockCode != null ? String(topStockCode) : undefined,
      originWarehouseName: resolvedOriginName ?? m.originWarehouseName,
      destinationWarehouseName: resolvedDestName ?? m.destinationWarehouseName,
      purchaseOrderNumber: topPoNum || m.purchaseOrderNumber,
      purchaseOrderId:
        m.purchaseOrderId != null && m.purchaseOrderId > 0
          ? m.purchaseOrderId
          : topPoId !== undefined && topPoId > 0
            ? topPoId
            : m.purchaseOrderId,
      purchaseOrderItemId:
        m.purchaseOrderItemId != null && m.purchaseOrderItemId > 0
          ? m.purchaseOrderItemId
          : topPoLineId !== undefined && topPoLineId > 0
            ? topPoLineId
            : m.purchaseOrderItemId,
      itemWarehouseName:
        topItemWarehouseName != null && String(topItemWarehouseName).trim()
          ? String(topItemWarehouseName)
          : undefined,
      ...(init !== undefined ? { initialExpectedQuantity: init } : {}),
      ...(pend !== undefined ? { pendingQuantity: pend } : {}),
      ...(recv !== undefined ? { entryReceiptReceived: recv } : {}),
      ...(topPending !== undefined ? { pendingItemEntryId: topPending } : {}),
      ...(linkedItemIdFromDto !== undefined && linkedItemIdFromDto > 0 ? { linkedItemId: linkedItemIdFromDto } : {}),
      ...(linkedItemNameFromDto ? { linkedItemName: linkedItemNameFromDto } : {}),
      ...(linkedItemCodeFromDto ? { linkedItemCode: linkedItemCodeFromDto } : {})
    };
  }

  private mapFromDto(dto: any): StockMovement {
    const source = dto?.movement ?? dto?.Movement ?? dto;
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);
    const trimStr = (v: any): string | undefined => {
      if (v === undefined || v === null || v === '') {
        return undefined;
      }
      const s = String(v).trim();
      return s || undefined;
    };

    const serviceOrderRequestId = pick(source, ['serviceOrderStockRequestId', 'ServiceOrderStockRequestId']);
    const requestNumber = pick(source, ['requestNumber', 'RequestNumber']);
    const priority = pick(source, ['priority', 'Priority']);
    const requestedAt = pick(source, ['requestedAt', 'RequestedAt']);

    const rawItemId =
      pick(source, ['ItemId', 'itemId', 'StockItemId', 'stockItemId']) ??
      pick(dto, ['ItemId', 'itemId', 'StockItemId', 'stockItemId']);
    const rawLinkedItemId =
      pick(source, ['LinkedItemId', 'linkedItemId']) ?? pick(dto, ['LinkedItemId', 'linkedItemId']);
    const rawLinkedName =
      pick(source, ['LinkedItemName', 'linkedItemName']) ?? pick(dto, ['LinkedItemName', 'linkedItemName']);
    const rawLinkedCode =
      pick(source, ['LinkedItemCode', 'linkedItemCode']) ?? pick(dto, ['LinkedItemCode', 'linkedItemCode']);
    let resolvedItemId: number | undefined;
    if (rawItemId !== undefined && rawItemId !== null && rawItemId !== '') {
      const n = Number(rawItemId);
      if (Number.isFinite(n) && n > 0) {
        resolvedItemId = n;
      }
    }
    if (resolvedItemId === undefined && rawLinkedItemId !== undefined && rawLinkedItemId !== null && rawLinkedItemId !== '') {
      const n = Number(rawLinkedItemId);
      if (Number.isFinite(n) && n > 0) {
        resolvedItemId = n;
      }
    }
    let linkedItemIdField: number | undefined;
    if (rawLinkedItemId !== undefined && rawLinkedItemId !== null && rawLinkedItemId !== '') {
      const n = Number(rawLinkedItemId);
      if (Number.isFinite(n) && n > 0) {
        linkedItemIdField = n;
      }
    }
    const linkedNameTrim = trimStr(rawLinkedName);
    const linkedCodeTrim = trimStr(rawLinkedCode);
    const rawStaffId = pick(source, ['StaffId', 'staffId']);
    const rawPendingEntry =
      pick(dto, [
        'pendingItemEntryId',
        'PendingItemEntryId',
        'pendingItemRequestEntryId',
        'PendingItemRequestEntryId',
        'pendingItemRequestId',
        'PendingItemRequestId'
      ]) ??
      pick(source, [
        'pendingItemEntryId',
        'PendingItemEntryId',
        'pendingItemRequestEntryId',
        'PendingItemRequestEntryId'
      ]);
    let pendingItemEntryId: number | undefined;
    if (rawPendingEntry !== undefined && rawPendingEntry !== null && rawPendingEntry !== '') {
      const pe = Number(rawPendingEntry);
      if (Number.isFinite(pe) && pe > 0) {
        pendingItemEntryId = pe;
      }
    }

    const rawPoId =
      pick(source, ['PurchaseOrderId', 'purchaseOrderId', 'PoId', 'poId']) ??
      pick(dto, ['PurchaseOrderId', 'purchaseOrderId', 'PoId', 'poId']);
    const rawPoLineId =
      pick(source, ['PurchaseOrderItemId', 'purchaseOrderItemId', 'PurchaseRequestItemId', 'purchaseRequestItemId']) ??
      pick(dto, ['PurchaseOrderItemId', 'purchaseOrderItemId', 'PurchaseRequestItemId', 'purchaseRequestItemId']);
    const rawPoNum =
      pick(source, [
        'PurchaseOrderNumber',
        'purchaseOrderNumber',
        'OrderNumber',
        'orderNumber',
        'PoNumber',
        'poNumber'
      ]) ??
      pick(dto, [
        'PurchaseOrderNumber',
        'purchaseOrderNumber',
        'OrderNumber',
        'orderNumber',
        'PoNumber',
        'poNumber'
      ]);
    let purchaseOrderId: number | undefined;
    if (rawPoId !== undefined && rawPoId !== null && rawPoId !== '') {
      const n = Number(rawPoId);
      if (Number.isFinite(n) && n > 0) {
        purchaseOrderId = n;
      }
    }
    let purchaseOrderItemId: number | undefined;
    if (rawPoLineId !== undefined && rawPoLineId !== null && rawPoLineId !== '') {
      const n = Number(rawPoLineId);
      if (Number.isFinite(n) && n > 0) {
        purchaseOrderItemId = n;
      }
    }
    let purchaseOrderNumber: string | undefined;
    if (rawPoNum !== undefined && rawPoNum !== null) {
      const s = String(rawPoNum).trim();
      if (s) {
        purchaseOrderNumber = s;
      }
    }

    return {
      id: pick(source, ['Id', 'id']) ?? serviceOrderRequestId ?? 0,
      itemId: resolvedItemId,
      movementType: pick(source, ['MovementType', 'movementType']) ?? 'Entry',
      quantity: pick(source, ['Quantity', 'quantity']) ?? 0,
      movementDate: toIso(pick(source, ['MovementDate', 'movementDate']) ?? requestedAt) ?? new Date().toISOString(),
      originWarehouseId:
        pick(source, ['OriginWarehouseId', 'originWarehouseId']) ??
        pick(dto, ['OriginWarehouseId', 'originWarehouseId']),
      destinationWarehouseId:
        pick(source, ['DestinationWarehouseId', 'destinationWarehouseId']) ??
        pick(dto, ['DestinationWarehouseId', 'destinationWarehouseId']),
      reason: pick(source, ['Reason', 'reason']) ?? priority ?? '',
      notes: pick(source, ['Notes', 'notes']),
      staffId: rawStaffId !== undefined && rawStaffId !== null ? Number(rawStaffId) : undefined,
      status: pick(source, ['Status', 'status']) ?? 'Pending',
      transferStatus: pick(source, ['TransferStatus', 'transferStatus']),
      serviceOrderId: pick(source, ['ServiceOrderId', 'serviceOrderId']),
      fiscalDocument: pick(source, ['FiscalDocument', 'fiscalDocument']),
      requiresApproval: pick(source, ['RequiresApproval', 'requiresApproval']) ?? false,
      createdAt: toIso(pick(source, ['CreatedAt', 'createdAt'])),
      updatedAt: toIso(pick(source, ['UpdatedAt', 'updatedAt'])),
      createdBy: pick(source, ['CreatedBy', 'createdBy']),
      updatedBy: pick(source, ['UpdatedBy', 'updatedBy']),
      ...(pendingItemEntryId !== undefined ? { pendingItemEntryId } : {}),

      // Dados relacionados
      itemCode:
        linkedCodeTrim ??
        trimStr(pick(source, ['ItemCode', 'itemCode'])) ??
        (requestNumber != null && requestNumber !== '' ? String(requestNumber) : undefined),
      itemName:
        linkedNameTrim ??
        trimStr(pick(source, ['ItemName', 'itemName'])) ??
        trimStr(pick(source, ['name', 'Name'])) ??
        (requestNumber ? String(requestNumber) : undefined),
      originWarehouseName: pick(source, ['OriginWarehouseName', 'originWarehouseName']),
      destinationWarehouseName: pick(source, ['DestinationWarehouseName', 'destinationWarehouseName']),
      staffName: pick(source, ['StaffName', 'staffName']),
      serviceOrderNumber: pick(source, ['ServiceOrderNumber', 'serviceOrderNumber']),
      ...(purchaseOrderId !== undefined ? { purchaseOrderId } : {}),
      ...(purchaseOrderItemId !== undefined ? { purchaseOrderItemId } : {}),
      ...(purchaseOrderNumber !== undefined ? { purchaseOrderNumber } : {}),
      ...(linkedItemIdField !== undefined ? { linkedItemId: linkedItemIdField } : {}),
      ...(linkedNameTrim ? { linkedItemName: linkedNameTrim } : {}),
      ...(linkedCodeTrim ? { linkedItemCode: linkedCodeTrim } : {})
    };
  }

  private mapListFromDto(list: any[]): StockMovement[] {
    if (!list || !Array.isArray(list)) {
      return [];
    }
    return list.map((x) => {
      const raw = this.unwrapStockMovementEnvelope(x);
      const m = this.mapFromDto(raw);
      return this.enrichMovementFromDtoRoot(raw, m);
    });
  }

  private mapToDto(payload: Partial<StockMovementCreate | StockMovementUpdate>): any {
    return {
      ItemId: payload.itemId,
      MovementType: payload.movementType,
      Quantity: payload.quantity,
      MovementDate: payload.movementDate ? new Date(payload.movementDate).toISOString() : null,
      OriginWarehouseId: payload.originWarehouseId,
      DestinationWarehouseId: payload.destinationWarehouseId,
      Reason: payload.reason,
      Notes: payload.notes,
      StaffId: payload.staffId,
      Status: payload.status,
      TransferStatus: payload.transferStatus,
      ServiceOrderId: payload.serviceOrderId,
      ServiceOrderNumber: payload.serviceOrderNumber,
      FiscalDocument: payload.fiscalDocument,
      RequiresApproval: payload.requiresApproval
    };
  }

  private mapPagedResponse(response: any): StockMovementPagedResult {
    const items = Array.isArray(response?.Items) ? response.Items : Array.isArray(response?.items) ? response.items : [];
    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return { items: this.mapListFromDto(items), page, pageSize, totalItems, totalPages };
  }
}
